import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";
import { readAiTokenDenial, KID_TOKENS_MESSAGE } from "./aiTokenError";
import { KID_FOODS } from "../experiments/kids-mode/kidFoods";
import { KID_TOOLS } from "../experiments/kids-mode/kidTools";

// Build a (emoji|name) key from a combination row.
const combinationKey = (emoji, name) => `${emoji}|${name}`;

// useKidCraft — Infinite-Craft state for the kid. Manages:
//   * inventory: items the kid has access to. Starts with KID_FOODS
//     base set; expands as combinations are discovered.
//   * combine(a, b): server call to the kid-craft-combine edge fn.
//   * realtime: new kid_craft_inventory rows flow in live (e.g.
//     when the kid plays on a second device).
//
// The inventory deduplicates by item_name so re-discovering the
// same combination doesn't double-add. The cache lives entirely
// server-side (emoji_combinations); the client never duplicates
// that.

export function useKidCraft(kidUserId) {
  const [inventoryRows, setInventoryRows] = useState([]);
  // imageByKey: Map(`${emoji}|${name}` → image_url) sourced from
  // emoji_combinations. Used to render the AI-generated sticker
  // when ready; falls back to emoji until the image lands.
  const [imageByKey, setImageByKey] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [combining, setCombining] = useState(false);
  const [error, setError] = useState(null);
  const chRef = useRef(null);
  const imgChRef = useRef(null);

  const loadInventory = useCallback(async () => {
    if (!kidUserId) {
      setInventoryRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from("kid_craft_inventory")
      .select("item_emoji, item_name, discovered_at, is_first_discovery")
      .eq("kid_user_id", kidUserId)
      .order("discovered_at", { ascending: false });
    if (error) {
      console.warn("[kid-craft] inventory load:", error.message);
      setError(error);
    }
    setInventoryRows(data || []);
    setLoading(false);
  }, [kidUserId]);

  // Pull image URLs for everything in the kid's inventory. We
  // query emoji_combinations matching the inventory's
  // (result_emoji, result_name) tuples. emoji_combinations is
  // public-read so no RLS friction.
  const loadImages = useCallback(async (rows) => {
    const list = rows || inventoryRows;
    if (!list.length) {
      setImageByKey(new Map());
      return;
    }
    // Postgrest doesn't natively do tuple-in-list, so we do two
    // IN-clause queries: one on result_emoji, one on result_name.
    // The set we care about is the intersection. In practice the
    // numbers are small (a kid's inventory is dozens), so this is
    // fine; we filter precisely on the client.
    const emojis = Array.from(new Set(list.map((r) => r.item_emoji)));
    const { data, error } = await supabase
      .from("emoji_combinations")
      .select("result_emoji, result_name, image_url, image_status")
      .in("result_emoji", emojis)
      .not("image_url", "is", null);
    if (error) {
      console.warn("[kid-craft] image lookup:", error.message);
      return;
    }
    const map = new Map();
    for (const r of (data || [])) {
      // Only the exact (emoji,name) match counts — two different
      // combos may collide on emoji but not name.
      const key = combinationKey(r.result_emoji, r.result_name);
      if (r.image_url) map.set(key, r.image_url);
    }
    setImageByKey(map);
  }, [inventoryRows]);

  useEffect(() => { loadInventory(); }, [loadInventory]);
  useEffect(() => { loadImages(inventoryRows); }, [loadImages, inventoryRows]);

  useEffect(() => {
    if (!kidUserId) return;
    const ch = safeChannel(`rt:kid_craft_inventory:${kidUserId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_craft_inventory", filter: `kid_user_id=eq.${kidUserId}` },
        () => loadInventory())
      .subscribe();
    chRef.current = ch;

    // Watch emoji_combinations for image_url UPDATEs so a tile
    // swaps from emoji → image when fal.ai finishes its work.
    const imgCh = safeChannel(`rt:emoji_combinations_images:${kidUserId}`)
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "emoji_combinations" },
        () => loadImages())
      .subscribe();
    imgChRef.current = imgCh;

    return () => {
      if (chRef.current) {
        supabase.removeChannel(chRef.current);
        chRef.current = null;
      }
      if (imgChRef.current) {
        supabase.removeChannel(imgChRef.current);
        imgChRef.current = null;
      }
    };
  }, [kidUserId, loadInventory, loadImages]);

  // Effective inventory with image URLs zipped in. Order:
  //   1. Pinned staples (always-useful: wheat, water, egg, milk,
  //      butter, cheese, honey, salt, oil) — first row of the grid.
  //   2. Recent discoveries (the kid's own unlocks, freshest first).
  //   3. Techniques (kept separately in the UI but included here
  //      so legacy callers can see them).
  //   4. Remaining base foods.
  const inventory = useMemo(() => {
    const discovered = inventoryRows.map((r) => ({
      emoji:            r.item_emoji,
      name:             r.item_name,
      imageUrl:         imageByKey.get(combinationKey(r.item_emoji, r.item_name)) || null,
      isDiscovery:      true,
      isFirstDiscovery: r.is_first_discovery,
      isTool:           false,
      pinned:           false,
    }));
    const dKeys = new Set(discovered.map((d) => combinationKey(d.emoji, d.name)));
    const tools = KID_TOOLS.map((t) => ({
      emoji:       t.emoji,
      name:        t.displayName,
      imageUrl:    null,
      isDiscovery: false,
      isTool:      true,
      pinned:      false,
    })).filter((t) => !dKeys.has(combinationKey(t.emoji, t.name)));
    const allBase = KID_FOODS
      .map((f) => ({
        emoji:       f.emoji,
        name:        f.displayName,
        imageUrl:    null,
        isDiscovery: false,
        isTool:      false,
        pinned:      f.pinned === true,
      }))
      .filter((b) => !dKeys.has(combinationKey(b.emoji, b.name)));
    const pinned   = allBase.filter((b) => b.pinned);
    const unpinned = allBase.filter((b) => !b.pinned);
    return [...pinned, ...discovered, ...tools, ...unpinned];
  }, [inventoryRows, imageByKey]);

  const combine = useCallback(async (ingredients, techniques) => {
    // Back-compat: legacy callers passed (a, b, technique).
    let ingArr = ingredients;
    if (!Array.isArray(ingredients) && ingredients?.emoji) {
      ingArr = [ingredients];
      if (techniques?.emoji && !techniques?.isTool) {
        ingArr.push(techniques);
        techniques = arguments[2];
      }
    }
    const items = (ingArr || []).filter((i) => i?.emoji);

    // Normalize techniques: accept array OR single object (legacy).
    const techArr = Array.isArray(techniques)
      ? techniques.filter((t) => t?.emoji)
      : (techniques?.emoji ? [techniques] : []);

    // Every combine REQUIRES a technique. The kid always picks an
    // action verb — no implicit "just combine."
    const minOk = items.length >= 1 && techArr.length >= 1;
    if (!minOk || combining) return null;

    setCombining(true);
    setError(null);
    try {
      const { data, error } = await supabase.functions.invoke("kid-craft-combine", {
        body: {
          ingredients: items.map((i) => ({ emoji: i.emoji, name: i.name })),
          techniques:  techArr.map((t) => ({ emoji: t.emoji, name: t.name })),
          // Debit the KID's weekly AI-token pool (docs/AI_TOKENS.md),
          // not the parent's — kid mode rides the parent's JWT.
          kidUserId,
        },
      });
      if (error) {
        const denial = await readAiTokenDenial(error, data);
        if (denial) { setError(KID_TOKENS_MESSAGE); return null; }
        throw error;
      }
      // Rejection: edge function couldn't validate a real recipe.
      // Surface as a soft "try again" — no cache row was written.
      if (data?.is_rejection) {
        setError(data.error || "Try a different combo!");
        return null;
      }
      if (data?.error) throw new Error(data.error);

      const result = {
        emoji:            data.result_emoji,
        name:             data.result_name,
        secondaryEmoji:   data.secondary_emoji || null,
        secondaryName:    data.secondary_name  || null,
        imageUrl:         data.image_url || null,
        isFirstDiscovery: data.is_first_discovery === true,
        wasCached:        data.was_cached === true,
        xpGained:         0,
        leveledUp:        false,
        newLevel:         null,
      };

      // Persist primary to per-kid inventory.
      const { error: invErr } = await supabase.from("kid_craft_inventory").insert({
        kid_user_id:        kidUserId,
        item_emoji:         result.emoji,
        item_name:          result.name,
        is_first_discovery: result.isFirstDiscovery,
      });
      const newToKid = !invErr;
      const dupe = invErr && String(invErr.message || "").includes("duplicate key");
      if (invErr && !dupe) {
        console.warn("[kid-craft] inventory insert:", invErr.message);
      }

      // Persist secondary byproduct if present — separate inventory
      // row keyed by its own (emoji, name). Same de-dup semantics:
      // duplicate key = already had it, that's fine.
      let secondaryNewToKid = false;
      if (result.secondaryEmoji && result.secondaryName) {
        const { error: secErr } = await supabase.from("kid_craft_inventory").insert({
          kid_user_id:        kidUserId,
          item_emoji:         result.secondaryEmoji,
          item_name:          result.secondaryName,
          is_first_discovery: false,
        });
        secondaryNewToKid = !secErr;
        if (secErr && !String(secErr.message || "").includes("duplicate key")) {
          console.warn("[kid-craft] secondary insert:", secErr.message);
        }
      }

      // XP award:
      //   First-ever discovery (global cache miss) → 25 XP
      //   New to this kid (cache hit)              → 10 XP
      //   Already had it                           → 0 XP
      //   Secondary byproduct new to this kid      → +5 XP bonus
      let xpAmount = 0;
      if (newToKid && result.isFirstDiscovery) xpAmount = 25;
      else if (newToKid)                       xpAmount = 10;
      if (secondaryNewToKid)                   xpAmount += 5;

      if (xpAmount > 0) {
        const { data: xpData, error: xpErr } = await supabase.rpc("award_kid_craft_xp", {
          p_kid_user_id: kidUserId,
          p_amount: xpAmount,
        });
        if (!xpErr && xpData) {
          const row = Array.isArray(xpData) ? xpData[0] : xpData;
          result.xpGained  = xpAmount;
          result.leveledUp = row?.leveled_up === true;
          result.newLevel  = row?.new_level || null;
        }
      }

      return result;
    } catch (e) {
      console.error("[kid-craft] combine:", e?.message || e);
      setError(e?.message || "combine failed");
      return null;
    } finally {
      setCombining(false);
    }
  }, [kidUserId, combining]);

  return {
    inventory,
    loading,
    combining,
    error,
    combine,
    refresh: loadInventory,
  };
}
